import { createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MenuItemType } from '@erikdakoda/ui/components/MenuItems';
import type {
  LabelCopyItemArgs,
  LabelMoveItemArgs,
  LabelRemoveItemArgs,
  LabelSidebarItem,
  LabelSidebarSection,
} from '../types';

export type LabelItemMenuContextValue = {
  /** Every section of the sidebar, unfiltered by the name filter. */
  sections: readonly LabelSidebarSection[];
  onMoveItem: (args: LabelMoveItemArgs) => Promise<void>;
  onCopyItem: (args: LabelCopyItemArgs) => Promise<void>;
  onRemoveItem: (args: LabelRemoveItemArgs) => Promise<void>;
};

/** Published by `LabelSidebar` so item rows can offer the touch path for copy / remove. */
export const LabelItemMenuContext = createContext<LabelItemMenuContextValue | null>(null);

/** Value published by each `LabelSection` for its item rows. */
export type LabelSectionContextValue = {
  section: LabelSidebarSection;
  /** Marks this section as the selected folder in the sidebar. */
  selectSection: () => void;
};

/** Published by `LabelSection` so an item row knows which section it is rendered in. */
export const LabelSectionContext = createContext<LabelSectionContextValue | null>(null);

type LabelUiTranslate = (key: string, options?: Record<string, string | number>) => string;

/** Result of `useLabelItemFavoriteMenuItems`. */
export type LabelItemFavoriteMenu = {
  /** Zero or one favorites toggle entry. */
  items: MenuItemType[];
  /** True while add/remove favorites is awaiting the sidebar mutation. */
  isPending: boolean;
};

/**
 * Returns a callback that selects the section containing the current item row.
 * No-ops when the row is rendered outside a `LabelSection`.
 * @returns A stable-enough select callback for the containing folder.
 */
export function useSelectContainingLabelSection(): () => void {
  const context = useContext(LabelSectionContext);
  return context?.selectSection ?? noopSelectSection;
}

function noopSelectSection(): void {
  // Outside a LabelSidebar there is no folder selection to update.
}

/**
 * Favorites add/remove menu entry for an item, using `LabelItemMenuContext` only (no
 * `LabelSectionContext`). Scans every section for the row so detail panels beside the sidebar can
 * toggle favorites the same way list rows do.
 *
 * Returns empty `items` when the context is missing, Favorites is absent, or the item is not in
 * any section. `isPending` is true while the copy/remove mutation is in flight.
 * @param itemId Id of the bot / prompt / metric the menu belongs to.
 * @returns Favorite menu entries and in-flight state.
 */
export function useLabelItemFavoriteMenuItems(itemId: string): LabelItemFavoriteMenu {
  const { t } = useTranslation('label-ui');
  const context = useContext(LabelItemMenuContext);
  const [isPending, setIsPending] = useState(false);

  const runFavoriteMutation = (work: () => Promise<void>): void => {
    setIsPending(true);
    void work().finally(() => {
      setIsPending(false);
    });
  };

  if (!context) {
    return { items: [], isPending: false };
  }

  const items = buildFavoriteMenuItems(itemId, context, t, {}, runFavoriteMutation).map((entry) => {
    if (entry.subHeader) {
      return entry;
    }
    return {
      ...entry,
      loading: isPending,
      disabled: isPending || entry.disabled,
    };
  });

  return { items, isPending };
}

/**
 * Folder membership entries for an item row's action menu, in order:
 * favorites toggle, move-to-folder (main sections only), add-to-folder, remove-from-this-folder
 * (main sections only — Favorites uses remove-from-favorites instead).
 *
 * Returns an empty list when the row is rendered outside a `LabelSidebar`, so item components can
 * spread the result unconditionally.
 * @param itemId Id of the bot / prompt / metric row the menu belongs to.
 * @returns Menu entries to splice into the row's own menu.
 */
export function useLabelItemMenuItems(itemId: string): MenuItemType[] {
  const { t } = useTranslation('label-ui');
  const context = useContext(LabelItemMenuContext);
  const sectionContext = useContext(LabelSectionContext);
  const section = sectionContext?.section ?? null;

  if (!context) {
    return [];
  }

  const { sections, onMoveItem, onCopyItem, onRemoveItem } = context;
  const item = section?.items.find((candidate) => candidate.id === itemId);
  if (!item) {
    return [];
  }

  const mainSections = sections.filter((candidate) => candidate.kind === 'main');
  const menuItems: MenuItemType[] = [
    ...buildFavoriteMenuItems(itemId, context, t, {
      preferredItem: item,
      preferredSourceSectionId: section?.id ?? null,
    }),
  ];

  if (section?.kind === 'main' && mainSections.length > 0) {
    menuItems.push({
      title: t('moveToLabel'),
      icon: 'folder-arrow-right',
      value: 'move-to-label',
      items: mainSections.map((destSection) => ({
        title: destSection.name,
        icon: destSection.icon,
        value: `move-to-label-${destSection.id}`,
        disabled:
          destSection.id === section.id ||
          destSection.items.some((candidate) => candidate.id === itemId),
        onClick: () => {
          void onMoveItem({
            item,
            sourceSectionId: section.id,
            sourceOrd: item.ord,
            destSectionId: destSection.id,
            destOrd: destSection.items.length,
          });
        },
      })),
    });
  }

  if (mainSections.length > 0) {
    menuItems.push({
      title: t('addToLabel'),
      icon: 'folder-plus',
      value: 'add-to-label',
      items: mainSections.map((destSection) => ({
        title: destSection.name,
        icon: destSection.icon,
        value: `add-to-label-${destSection.id}`,
        disabled: destSection.items.some((candidate) => candidate.id === itemId),
        onClick: () => {
          void onCopyItem({
            item,
            sourceSectionId: section?.id ?? null,
            destSection,
            destOrd: destSection.items.length,
          });
        },
      })),
    });
  }

  if (section?.kind === 'main') {
    menuItems.push({
      title: t('removeFromLabel'),
      icon: 'circle-minus',
      value: 'remove-from-label',
      onClick: () => {
        void onRemoveItem({ item, section });
      },
    });
  }

  return menuItems;
}

type FavoriteMenuSource = {
  preferredItem?: LabelSidebarItem;
  preferredSourceSectionId?: string | null;
};

/**
 * Builds the single favorites toggle entry when a Favorites section exists.
 * @param itemId Item to add or remove.
 * @param context Sidebar menu callbacks and sections.
 * @param t label-ui translator.
 * @param source Optional preferred source row when the caller already resolved the section item.
 * @param runMutation Optional wrapper that tracks pending state around the copy/remove promise.
 */
function buildFavoriteMenuItems(
  itemId: string,
  context: LabelItemMenuContextValue,
  t: LabelUiTranslate,
  source: FavoriteMenuSource = {},
  runMutation?: (work: () => Promise<void>) => void,
): MenuItemType[] {
  const { sections, onCopyItem, onRemoveItem } = context;
  const favoritesSection = sections.find((candidate) => candidate.kind === 'secondary');
  if (!favoritesSection) {
    return [];
  }

  const startMutation = (work: () => Promise<void>): void => {
    if (runMutation) {
      runMutation(work);
      return;
    }
    void work();
  };

  const favoritesItem = favoritesSection.items.find((candidate) => candidate.id === itemId);
  if (favoritesItem) {
    return [
      {
        title: t('removeFromFavorites'),
        icon: 'heart',
        prefix: 'fas',
        color: 'info',
        hoverIcon: 'heart-slash',
        value: 'remove-from-favorites',
        onClick: () => {
          startMutation(() => onRemoveItem({ item: favoritesItem, section: favoritesSection }));
        },
      },
    ];
  }

  const resolved = resolveFavoriteSourceItem(itemId, sections, source);
  if (!resolved) {
    return [];
  }

  return [
    {
      title: t('addToFavorites'),
      icon: 'heart',
      value: 'add-to-favorites',
      onClick: () => {
        startMutation(() =>
          onCopyItem({
            item: resolved.item,
            sourceSectionId: resolved.sourceSectionId,
            destSection: favoritesSection,
            destOrd: favoritesSection.items.length,
          }),
        );
      },
    },
  ];
}

/**
 * Picks a sidebar row to copy into Favorites, preferring the caller's section then any non-Favorites
 * section that already lists the item.
 */
function resolveFavoriteSourceItem(
  itemId: string,
  sections: readonly LabelSidebarSection[],
  source: FavoriteMenuSource,
): { item: LabelSidebarItem; sourceSectionId: string | null } | null {
  if (source.preferredItem) {
    return {
      item: source.preferredItem,
      sourceSectionId: source.preferredSourceSectionId ?? null,
    };
  }

  for (const section of sections) {
    if (section.kind === 'secondary') {
      continue;
    }
    const found = section.items.find((candidate) => candidate.id === itemId);
    if (found) {
      return { item: found, sourceSectionId: section.id };
    }
  }

  return null;
}
